import { Info } from "lucide-react";
import { inr } from "@/lib/format";
import { keyed } from "@/lib/keys";

const Block = ({ title, children, testid }) => (
  <section className="border-t border-slate-200 py-8" data-testid={testid}>
    <h2 className="mb-4 text-xl font-bold text-brand-ink md:text-2xl">{title}</h2>
    {children}
  </section>
);

const Para = ({ text }) => keyed((text || "").split(/\n\s*\n/)).map(([k, p]) => <p key={k} className="mb-4 text-base leading-relaxed text-slate-700">{p}</p>);

const Detail = ({ label, value }) =>
  value ? (
    <div className="grid gap-1 py-3 sm:grid-cols-3 sm:gap-4">
      <dt className="text-sm font-semibold text-brand-ink">{label}</dt>
      <dd className="text-base text-slate-700 sm:col-span-2">{value}</dd>
    </div>
  ) : null;

export const ProductDisclaimer = ({ text, className = "" }) => (
  <div className={`flex gap-3 rounded-2xl border border-amber-200 bg-amber-50 p-4 ${className}`} data-testid="product-disclaimer">
    <Info className="mt-0.5 h-5 w-5 shrink-0 text-amber-800" aria-hidden="true" />
    <p className="text-sm leading-relaxed text-amber-950">{text}</p>
  </div>
);

export const ProductDetails = ({ product: p }) => (
  <div className="max-w-3xl">
    <Block title="Description" testid="product-description">
      <Para text={p.description} />
      {p.origin_note && <p className="text-base font-medium text-brand-blue">{p.origin_note}</p>}
    </Block>
    {p.composition?.length > 0 && (
      <Block title="Composition" testid="product-composition">
        {p.composition_heading && <p className="mb-3 text-base text-slate-700">{p.composition_heading}:</p>}
        <div className="overflow-hidden rounded-2xl ring-1 ring-slate-200">
          <table className="w-full text-left text-base">
            <thead className="bg-brand-tint text-sm text-brand-ink">
              <tr><th scope="col" className="px-4 py-3 font-semibold">Ingredient</th><th scope="col" className="px-4 py-3 text-right font-semibold">Quantity</th></tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {p.composition.map((r) => (
                <tr key={r.ingredient}><td className="px-4 py-3 text-slate-700">{r.ingredient}</td><td className="whitespace-nowrap px-4 py-3 text-right text-slate-700">{r.quantity}</td></tr>
              ))}
            </tbody>
          </table>
        </div>
        {p.composition_note && <p className="mt-3 text-sm text-slate-600">{p.composition_note}</p>}
      </Block>
    )}
    <Block title="Recommended usage" testid="product-usage"><Para text={p.usage} /></Block>
    <Block title="Precautions" testid="product-precautions"><Para text={p.precautions} /></Block>
    <Block title="Storage" testid="product-storage">
      <Para text={p.storage} />
      {p.shelf_life && <p className="text-base text-slate-700"><span className="font-semibold text-brand-ink">Shelf life:</span> {p.shelf_life}.</p>}
    </Block>
    <Block title="Product information" testid="product-information">
      <dl className="divide-y divide-slate-100">
        <Detail label="Product type" value={p.product_type} />
        <Detail label="Pack size" value={p.pack_size} />
        <Detail label="MRP" value={`${inr(p.mrp)} (inclusive of all taxes)`} />
        <Detail label="Country of origin" value={p.country_of_origin} />
        <Detail label="Manufactured by" value={p.manufacturer} />
        <Detail label="Mfg. Lic. No." value={p.mfg_license} />
        <Detail label="Marketed by" value={p.marketer} />
        <Detail label="Sold by" value={p.seller} />
        <Detail label="Customer support" value={[p.support_phone, p.support_email].filter(Boolean).join(" · ")} />
      </dl>
    </Block>
    {p.disclaimer && <ProductDisclaimer text={p.disclaimer} />}
  </div>
);
